// Item #43 — aba financeira do God Mode. Backend já pronto
// (routes/godmode.js: /assinaturas/entidades/:idEntidade/{painel,assinaturas,eventos,cobrancas}).
import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { BASE_URL } from "../../Utils/api";
import {
  Alert,
  Badge,
  Card,
  Col,
  Form,
  ListGroup,
  Nav,
  Pagination,
  Row,
  Spinner,
  Table,
  Tab,
} from "react-bootstrap";
import { ErroModal } from "../Generals";
import "./GodMode.css";


const BADGES_STATUS_ASSINATURA = {
  ATIVA: "success",
  PENDENTE_PAGAMENTO: "warning",
  CANCELADA: "secondary",
  INADIMPLENTE: "danger",
};

const BADGES_STATUS_COBRANCA = {
  PENDING: "warning",
  OVERDUE: "danger",
  CONFIRMED: "success",
  RECEIVED: "success",
  RECEIVED_IN_CASH: "success",
  CANCELLED: "secondary",
  REFUNDED: "dark",
};

function formatarDataHora(valor) {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? String(valor) : data.toLocaleString("pt-BR");
}

function formatarData(valor) {
  if (!valor) return "—";
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? String(valor) : data.toLocaleDateString("pt-BR");
}

function formatarMoeda(valor) {
  if (valor == null) return "—";
  return Number(valor).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
}

function nomeEntidade(entidade) {
  return entidade.fantasia || entidade.razao_social || `Entidade #${entidade.id}`;
}

function PaginacaoSimples({ pagina, totalPaginas, onChange }) {
  if (!totalPaginas || totalPaginas <= 1) return null;
  return (
    <Pagination className="justify-content-center mt-2">
      <Pagination.Prev disabled={pagina <= 1} onClick={() => onChange(Math.max(1, pagina - 1))} />
      <Pagination.Item active>{pagina}</Pagination.Item>
      <Pagination.Next
        disabled={pagina >= totalPaginas}
        onClick={() => onChange(Math.min(totalPaginas, pagina + 1))}
      />
    </Pagination>
  );
}

function FinanceiroGodMode() {
  const token = localStorage.getItem("authToken");
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  const [busca, setBusca] = useState("");
  const [buscaDebounced, setBuscaDebounced] = useState("");
  const [resultados, setResultados] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [entidadeSelecionada, setEntidadeSelecionada] = useState(null);

  const [painel, setPainel] = useState(null);
  const [carregandoPainel, setCarregandoPainel] = useState(false);
  const [erro, setErro] = useState("");

  const [abaLista, setAbaLista] = useState("assinaturas");
  const [listaDados, setListaDados] = useState({ data: [], paginacao: null });
  const [carregandoLista, setCarregandoLista] = useState(false);
  const [pagina, setPagina] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setBuscaDebounced(busca.trim()), 400);
    return () => clearTimeout(timer);
  }, [busca]);

  useEffect(() => {
    if (!buscaDebounced) {
      setResultados([]);
      return;
    }
    let cancelado = false;
    setBuscando(true);
    api
      .get(`${BASE_URL}/api/godmode/integracoes/asaas/entidades`, {
        headers,
        params: { busca: buscaDebounced, pagina: 1, limite: 10 },
      })
      .then(({ data }) => {
        if (!cancelado) setResultados(Array.isArray(data?.data) ? data.data : []);
      })
      .catch(() => {
        if (!cancelado) setResultados([]);
      })
      .finally(() => {
        if (!cancelado) setBuscando(false);
      });
    return () => {
      cancelado = true;
    };
  }, [buscaDebounced, headers]);

  const selecionarEntidade = (entidade) => {
    setEntidadeSelecionada(entidade);
    setResultados([]);
    setBusca("");
    setAbaLista("assinaturas");
    setPagina(1);
  };

  const carregarPainel = useCallback(async () => {
    if (!entidadeSelecionada) return;
    setCarregandoPainel(true);
    setErro("");
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/godmode/assinaturas/entidades/${entidadeSelecionada.id}/painel`,
        { headers }
      );
      setPainel(data?.data || null);
    } catch (err) {
      setPainel(null);
      setErro(err.response?.data?.message || "Não foi possível carregar o painel financeiro.");
    } finally {
      setCarregandoPainel(false);
    }
  }, [entidadeSelecionada, headers]);

  useEffect(() => {
    carregarPainel();
  }, [carregarPainel]);

  const carregarLista = useCallback(async () => {
    if (!entidadeSelecionada) return;
    setCarregandoLista(true);
    try {
      const { data } = await api.get(
        `${BASE_URL}/api/godmode/assinaturas/entidades/${entidadeSelecionada.id}/${abaLista}`,
        { headers, params: { pagina, limite: 20 } }
      );
      setListaDados({ data: Array.isArray(data?.data) ? data.data : [], paginacao: data?.paginacao || null });
    } catch (err) {
      setListaDados({ data: [], paginacao: null });
    } finally {
      setCarregandoLista(false);
    }
  }, [entidadeSelecionada, abaLista, pagina, headers]);

  useEffect(() => {
    carregarLista();
  }, [carregarLista]);

  useEffect(() => {
    setPagina(1);
  }, [abaLista]);

  return (
    <div>
      <Card className="mb-3">
        <Card.Body>
          <Form.Label>Buscar entidade</Form.Label>
          <Form.Control
            value={busca}
            onChange={(event) => setBusca(event.target.value)}
            placeholder="Nome, CNPJ, ID ou identificador Asaas"
          />
          {buscando && <Spinner animation="border" size="sm" className="mt-2" />}
          {resultados.length > 0 && (
            <ListGroup className="mt-2">
              {resultados.map((entidade) => (
                <ListGroup.Item
                  key={entidade.id}
                  action
                  onClick={() => selecionarEntidade(entidade)}
                >
                  {nomeEntidade(entidade)}{" "}
                  <small className="text-muted">(#{entidade.id} — {entidade.cnpj || "sem CNPJ"})</small>
                </ListGroup.Item>
              ))}
            </ListGroup>
          )}
        </Card.Body>
      </Card>

      {!entidadeSelecionada && (
        <Alert variant="info">Busque e selecione uma entidade para ver o financeiro.</Alert>
      )}

      <ErroModal erro={erro} />

      {entidadeSelecionada && (
        <>
          <Card className="mb-3">
            <Card.Header className="d-flex justify-content-between align-items-center">
              <span>{nomeEntidade(entidadeSelecionada)}</span>
              <small className="text-muted">#{entidadeSelecionada.id}</small>
            </Card.Header>
            <Card.Body>
              {carregandoPainel ? (
                <div className="d-flex justify-content-center py-3">
                  <Spinner animation="border" />
                </div>
              ) : painel?.resumo ? (
                <Row className="g-3">
                  <Col md={3}>
                    <div className="text-muted small">Plano atual</div>
                    <div>
                      <strong>{painel.resumo.assinatura?.plano_nome || "—"}</strong>{" "}
                      <Badge bg={BADGES_STATUS_ASSINATURA[painel.resumo.assinatura?.status] || "secondary"}>
                        {painel.resumo.assinatura?.status || "—"}
                      </Badge>
                    </div>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted small">Ciclo</div>
                    <div>{painel.resumo.assinatura?.ciclo_cobranca || "—"}</div>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted small">Próximo vencimento</div>
                    <div>{formatarData(painel.resumo.assinatura?.proximo_vencimento)}</div>
                  </Col>
                  <Col md={3}>
                    <div className="text-muted small">Forma de pagamento</div>
                    <div>{painel.resumo.assinatura?.billing_type || "—"}</div>
                  </Col>
                </Row>
              ) : (
                <span className="text-muted">Sem dados de plano para esta entidade.</span>
              )}
            </Card.Body>
          </Card>

          <Tab.Container activeKey={abaLista} onSelect={(k) => k && setAbaLista(k)}>
            <Nav variant="tabs" className="mb-3">
              <Nav.Item>
                <Nav.Link eventKey="assinaturas">Assinaturas</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="eventos">Eventos</Nav.Link>
              </Nav.Item>
              <Nav.Item>
                <Nav.Link eventKey="cobrancas">Cobranças</Nav.Link>
              </Nav.Item>
            </Nav>

            {carregandoLista ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <>
                {abaLista === "assinaturas" && (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>ID</th>
                          <th>Plano</th>
                          <th>Ciclo</th>
                          <th>Status</th>
                          <th>Próximo vencimento</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listaDados.data.length === 0 ? (
                          <tr>
                            <td colSpan={5} className="text-center text-muted py-3">
                              Nenhuma assinatura encontrada.
                            </td>
                          </tr>
                        ) : (
                          listaDados.data.map((item) => (
                            <tr key={item.id}>
                              <td>{item.id}</td>
                              <td>{item.plano_nome || item.plano_codigo}</td>
                              <td>{item.ciclo_cobranca}</td>
                              <td>
                                <Badge bg={BADGES_STATUS_ASSINATURA[item.status] || "secondary"}>
                                  {item.status}
                                </Badge>
                              </td>
                              <td>{formatarData(item.proximo_vencimento)}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {abaLista === "eventos" && (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>Data</th>
                          <th>Ação</th>
                          <th>Origem</th>
                          <th>Resultado</th>
                          <th>Usuário</th>
                          <th>Motivo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {listaDados.data.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-3">
                              Nenhum evento encontrado.
                            </td>
                          </tr>
                        ) : (
                          listaDados.data.map((item) => (
                            <tr key={item.id}>
                              <td>{formatarDataHora(item.created_at)}</td>
                              <td>{item.acao}</td>
                              <td>{item.origem}</td>
                              <td>{item.resultado}</td>
                              <td>{item.usuario_nome || "—"}</td>
                              <td>{item.motivo || "—"}</td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                {abaLista === "cobrancas" && (
                  <div className="table-responsive">
                    <Table hover className="align-middle">
                      <thead>
                        <tr>
                          <th>Vencimento</th>
                          <th>Status</th>
                          <th>Valor</th>
                          <th>Pagamento</th>
                          <th>Forma</th>
                          <th></th>
                        </tr>
                      </thead>
                      <tbody>
                        {listaDados.data.length === 0 ? (
                          <tr>
                            <td colSpan={6} className="text-center text-muted py-3">
                              Nenhuma cobrança encontrada.
                            </td>
                          </tr>
                        ) : (
                          listaDados.data.map((item) => (
                            <tr key={item.id}>
                              <td>{formatarData(item.data_vencimento)}</td>
                              <td>
                                <Badge bg={BADGES_STATUS_COBRANCA[item.status] || "secondary"}>
                                  {item.status}
                                </Badge>
                              </td>
                              <td>{formatarMoeda(item.valor)}</td>
                              <td>{formatarData(item.data_pagamento)}</td>
                              <td>{item.billing_type || "—"}</td>
                              <td>
                                {item.invoice_url && (
                                  <a href={item.invoice_url} target="_blank" rel="noopener noreferrer">
                                    Ver fatura
                                  </a>
                                )}
                              </td>
                            </tr>
                          ))
                        )}
                      </tbody>
                    </Table>
                  </div>
                )}

                <PaginacaoSimples
                  pagina={pagina}
                  totalPaginas={listaDados.paginacao?.totalPaginas}
                  onChange={setPagina}
                />
              </>
            )}
          </Tab.Container>
        </>
      )}
    </div>
  );
}

export default FinanceiroGodMode;
